import { useState, useRef, useCallback } from "react";
import { yayinTarihiLteNow } from "../lib/yayinFilter";

export function useSearch(options) {
	const {
		newsBaseEndpoint = "/api/habers",
		annEndpoint = "/api/duyurulars?populate=*&pagination[pageSize]=2000&sort=createdAt:desc",
		eventsEndpoint = "/api/events?populate=*",
		servicesEndpoint = "/api/hizmetlers?fields=id,slug,adi,arama,icerik,url&populate[fotograf][fields][1]=url&pagination[pageSize]=2000",
		debounceMs = 300,
		newsLimit = 50,
		apiBase = "",
	} = options || {};

	const [query, setQueryState] = useState("");
	const [newsResults, setNewsResults] = useState([]);
	const [annResults, setAnnResults] = useState([]);
	const [eventsResults, setEventsResults] = useState([]);
	const [servicesResults, setServicesResults] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const timer = useRef(null);
	const abortCtrl = useRef(null);

	const extractTextFromRich = (rich) => {
		if (!rich) return "";
		if (typeof rich === "string") return rich;
		if (Array.isArray(rich)) {
			return rich
				.map((node) => {
					if (!node) return "";
					if (typeof node === "string") return node;
					if (node.type === "text") return node.text || "";
					if (node.children) return extractTextFromRich(node.children);
					return "";
				})
				.join(" ");
		}
		if (rich.children) return extractTextFromRich(rich.children);
		return "";
	};

	const pickImageUrl = (imgObj) => {
		if (!imgObj) return null;
		const formats = imgObj.formats || {};
		// Sadece medium formatı kullanılacak
		const mediumUrl = formats.medium && formats.medium.url ? formats.medium.url : null;
		const found = mediumUrl || imgObj.url || null;
		if (!found) return null;
		return found.startsWith("http") ? found : apiBase.replace(/\/$/, "") + found;
	};

	const matchText = (haystack, needle) => {
		if (!needle) return false;
		return haystack.toLocaleLowerCase("tr").includes(needle.toLocaleLowerCase("tr"));
	};

	// Strapi tabanlı haber arama fonksiyonu
	const searchNewsInStrapi = async (searchTerm) => {
		if (!searchTerm) {
			// Eğer arama terimi yoksa, tüm haberleri getir
			const url = `${newsBaseEndpoint}?fields=baslik,icerik,durum,aciklama,yayin_tarihi,slug&populate[kapak_fotografi][fields][0]=url&sort=yayin_tarihi:desc&pagination[pageSize]=${newsLimit}&${yayinTarihiLteNow()}`;
			const response = await fetch(url, { signal: abortCtrl.current.signal });
			if (!response.ok) throw new Error("Haber API hatası: " + response.status);
			const data = await response.json();
			return Array.isArray(data.data) ? data.data : [];
		}

		let searchResults = [];
		let page = 1;
		
		do {
			// Hem baslik hem de aciklama alanlarında arama yap
			const url = `${newsBaseEndpoint}?filters[$or][0][baslik][$containsi]=${encodeURIComponent(searchTerm)}&filters[$or][1][aciklama][$containsi]=${encodeURIComponent(searchTerm)}&pagination[page]=${page}&pagination[pageSize]=100&fields=baslik,icerik,durum,aciklama,yayin_tarihi,slug&populate[kapak_fotografi][fields][0]=url&sort=yayin_tarihi:desc&${yayinTarihiLteNow()}`;
			
			const response = await fetch(url, { signal: abortCtrl.current.signal });
			if (!response.ok) throw new Error("Haber API hatası: " + response.status);
			
			const data = await response.json();
			searchResults.push(...data.data);
			page++;
			
			// Tüm sayfalarda arama bitene kadar devam et veya limit'e ulaş
			if (data.data.length < 100 || searchResults.length >= newsLimit) break;
			
		} while (true);
		
		return searchResults.slice(0, newsLimit);
	};

	const runSearch = useCallback(
		async (q) => {
			if (abortCtrl.current) abortCtrl.current.abort();
			abortCtrl.current = new AbortController();
			setLoading(true);
			setError(null);
			try {
				const [newsData, annResp, eventsResp, servicesResp] = await Promise.all([
					searchNewsInStrapi(q),
					fetch(annEndpoint, { signal: abortCtrl.current.signal }),
					fetch(eventsEndpoint, { signal: abortCtrl.current.signal }),
					fetch(servicesEndpoint, { signal: abortCtrl.current.signal }),
				]);

				if (!annResp.ok) throw new Error("Duyuru API hatası: " + annResp.status);
				if (!eventsResp.ok) throw new Error("Etkinlik API hatası: " + eventsResp.status);
				if (!servicesResp.ok) throw new Error("Hizmetler API hatası: " + servicesResp.status);

				// Haber sonuçlarını işle (Strapi'den gelen veri zaten filtrelenmiş)
				const matchedNews = newsData.map(item => {
					const baslik = item.baslik || (item.attributes && item.attributes.baslik) || "";
					const aciklama = item.aciklama || (item.attributes && item.attributes.aciklama) || "";
					const kapak = item.kapak_fotografi || (item.attributes && item.attributes.kapak_fotografi);
					const kapakUrl = kapak && kapak.url ? (kapak.url.startsWith("http") ? kapak.url : apiBase.replace(/\/$/, "") + kapak.url) : null;
					const publishedAt = item.yayin_tarihi || (item.attributes && item.attributes.yayin_tarihi) || item.publishedAt || null;
					
					return {
						id: item.id || (item.attributes && item.attributes.id),
						slug: item.slug || (item.attributes && item.attributes.slug) || "",
						baslik,
						aciklama,
						kapakUrl,
						publishedAt,
						durum: item.durum || (item.attributes && item.attributes.durum) || null,
					};
				});
				
				setNewsResults(matchedNews);

				const annJson = await annResp.json();
				const annData = Array.isArray(annJson.data) ? annJson.data : [];
				const matchedAnn = [];
				for (const item of annData) {
					const baslik = item.baslik || (item.attributes && item.attributes.baslik) || "";
					const icerik = item.icerik || (item.attributes && item.attributes.icerik) || [];
					const contentText = extractTextFromRich(icerik);
					const hay = [baslik, contentText].join(" ");
					if (!q || matchText(hay, q)) {
						matchedAnn.push({
							id: item.id || (item.attributes && item.attributes.id),
							baslik,
							duyuru_turu: item.duyuru_turu || (item.attributes && item.attributes.duyuru_turu) || null,
							baslangic_tarihi: item.baslangic_tarihi || (item.attributes && item.attributes.baslangic_tarihi) || null,
							icerik: contentText,
						});
					}
				}
				setAnnResults(matchedAnn);
				// Events: search by name, description, and events field (event type)
				const eventsJson = await eventsResp.json();
				const eventsData = Array.isArray(eventsJson.data) ? eventsJson.data : [];
				const matchedEvents = [];
				for (const item of eventsData) {
					const name = item.name || (item.attributes && item.attributes.name) || "";
					const desc = item.description || (item.attributes && item.attributes.description) || [];
					const descText = extractTextFromRich(desc);
					const eventType = item.events || (item.attributes && item.attributes.events) || "";
					const locationName = item.location?.name || (item.attributes && item.attributes.location?.name) || "";
					
					// Include name, description, event type, and location in search
					const hay = [name, descText, eventType, locationName].join(" ");
					if (!q || matchText(hay, q)) {
						const img = item.image || (item.attributes && item.attributes.image);
						const imgData = img && (img.data || img);
						const imageUrl = pickImageUrl(imgData);
						matchedEvents.push({
							id: item.id || (item.attributes && item.attributes.id),
							slug: item.slug || (item.attributes && item.attributes.slug) || "",
							name,
							imageUrl,
							description: descText,
							events: eventType,
							location: item.location || (item.attributes && item.attributes.location),
							create: item.create || (item.attributes && item.attributes.create),
							price: item.price || (item.attributes && item.attributes.price) || 0,
							quota: item.quota || (item.attributes && item.attributes.quota),
							ticket: item.ticket || (item.attributes && item.attributes.ticket),
						});
					}
				}
				setEventsResults(matchedEvents);
				// Services (Hizmetler): only items with arama === true
				const servicesJson = await servicesResp.json();
				const servicesData = Array.isArray(servicesJson.data) ? servicesJson.data : [];
				const matchedServices = [];
				for (const item of servicesData) {
					// some entries have fields at top-level or under attributes
					const adi = item.adi || (item.attributes && item.attributes.adi) || "";
					// only include if arama is truthy true
					const aramaFlag = (item.arama !== undefined) ? item.arama : (item.attributes && item.attributes.arama);
					if (!aramaFlag) continue;
					const icerik = item.icerik || (item.attributes && item.attributes.icerik) || [];
					const text = extractTextFromRich(icerik);
					const hay = [adi, text].join(" ");
					if (!q || matchText(hay, q)) {
						const foto = item.fotograf || (item.attributes && item.attributes.fotograf) || null;
						// fotograf may be array or object
						let fotoData = null;
						if (Array.isArray(foto) && foto.length) fotoData = foto[0];
						else fotoData = foto && (foto.data || foto);
						const imageUrl = pickImageUrl(fotoData);
						matchedServices.push({
							id: item.id || (item.attributes && item.attributes.id),
							slug: item.slug || (item.attributes && item.attributes.slug) || "",
							adi,
							imageUrl,
							description: text,
							url: item.url || (item.attributes && item.attributes.url) || null,
						});
					}
				}
				setServicesResults(matchedServices);
			} catch (err) {
				if (err && err.name === "AbortError") {
					// aborted by new request - ignore
				} else {
					setError(err && err.message ? err.message : "Arama sırasında hata oluştu");
				}
			} finally {
				setLoading(false);
			}
		},
		[newsBaseEndpoint, annEndpoint, eventsEndpoint, servicesEndpoint, newsLimit, apiBase]
	);

	// Yeni: sadece Hizmetler endpoint'ini hızlıca getirir (odaklandığında öneri için kullanılır)
	const fetchServices = useCallback(
		async (q) => {
			if (abortCtrl.current) abortCtrl.current.abort();
			abortCtrl.current = new AbortController();
			setLoading(true);
			setError(null);
			try {
				const servicesResp = await fetch(servicesEndpoint, { signal: abortCtrl.current.signal });
				if (!servicesResp.ok) throw new Error("Hizmetler API hatası: " + servicesResp.status);
				const servicesJson = await servicesResp.json();
				const servicesData = Array.isArray(servicesJson.data) ? servicesJson.data : [];
				const matchedServices = [];
				for (const item of servicesData) {
					const adi = item.adi || (item.attributes && item.attributes.adi) || "";
					const aramaFlag = (item.arama !== undefined) ? item.arama : (item.attributes && item.attributes.arama);
					if (!aramaFlag) continue;
					const icerik = item.icerik || (item.attributes && item.attributes.icerik) || [];
					const text = extractTextFromRich(icerik);
					const hay = [adi, text].join(" ");
					if (!q || matchText(hay, q)) {
						const foto = item.fotograf || (item.attributes && item.attributes.fotograf) || null;
						let fotoData = null;
						if (Array.isArray(foto) && foto.length) fotoData = foto[0];
						else fotoData = foto && (foto.data || foto);
						const imageUrl = pickImageUrl(fotoData);
						matchedServices.push({
							id: item.id || (item.attributes && item.attributes.id),
							slug: item.slug || (item.attributes && item.attributes.slug) || "",
							adi,
							imageUrl,
							description: text,
							url: item.url || (item.attributes && item.attributes.url) || null,
						});
					}
				}
				setServicesResults(matchedServices);
			} catch (err) {
				if (err && err.name === "AbortError") {
					// aborted by new request - ignore
				} else {
					setError(err && err.message ? err.message : "Hizmetler alınırken hata oluştu");
				}
			} finally {
				setLoading(false);
			}
		},
		[servicesEndpoint, apiBase]
	);

	const search = useCallback(
		(q) => {
			setQueryState(q);
			if (timer.current) window.clearTimeout(timer.current);
			timer.current = window.setTimeout(() => {
				runSearch((q || "").trim());
			}, debounceMs);
		},
		[runSearch, debounceMs]
	);

	const clear = useCallback(() => {
		setQueryState("");
		setNewsResults([]);
		setAnnResults([]);
		setEventsResults([]);
		setServicesResults([]);
		setError(null);
		if (timer.current) window.clearTimeout(timer.current);
		if (abortCtrl.current) abortCtrl.current.abort();
	}, []);

	const navigateToNews = useCallback((slug) => {
		if (!slug) return;
		window.location.href = `https://serdivan.bel.tr/haberler/${slug}`;
	}, []);

	// navigate to service: if url present go there, otherwise go to local hizmetler slug page
	const navigateToService = useCallback((item) => {
		if (!item) return;
		if (item.url) {
			// absolute or relative url -> open directly
			window.location.href = item.url;
			return;
		}
		const slug = item.slug || item.adi && item.adi.replace(/\s+/g, "-").toLowerCase();
		if (slug) window.location.href = `https://serdivan.bel.tr/hizmetler/${slug}`;
	}, []);

	const triggerNow = useCallback(
		(q) => {
			if (timer.current) window.clearTimeout(timer.current);
			runSearch((q === undefined || q === null ? query : q).trim());
		},
		[runSearch, query]
	);

	return {
		query,
		setQuery: search,
		results: {
			news: newsResults,
			announcements: annResults,
			events: eventsResults,
			services: servicesResults,
		},
		loading,
		error,
		clear,
		navigateToNews,
		navigateToService,
		triggerNow,
		fetchServices, // yeni expose
	};
}